import {
  Badge,
  Box,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useBreakpointValue,
} from '@chakra-ui/react';
import { MdMoreVert } from 'react-icons/md';
import {
  ApiKeyDto,
  ApiKeyEnvironment,
  ScopeDescription,
} from '../../../models/api-key/dto/api-key.dto';
import { copy } from '../copy';
import { ScopeChipList } from './ScopeChip';
import { StatusPill } from './StatusPill';

type Props = {
  apiKeys: ApiKeyDto[];
  scopes: ScopeDescription[];
  onRevoke: (apiKey: ApiKeyDto) => void;
};

const STALE_DAYS = 90;

function relativeDays(isoDate: string): number {
  return Math.floor((Date.now() - new Date(isoDate).getTime()) / 86_400_000);
}

function usedLabel(apiKey: ApiKeyDto): string {
  if (!apiKey.lastUsedAt) return copy.neverUsed;
  const days = relativeDays(apiKey.lastUsedAt);
  if (days < 1) return copy.today;
  if (days === 1) return copy.yesterday;
  if (days > STALE_DAYS) return copy.staleLabel(days);
  return copy.daysAgo(days);
}

function mask(apiKey: ApiKeyDto): string {
  return `${apiKey.keyPrefix}\u2022\u2022\u2022\u2022\u2022${apiKey.keyId.slice(
    -4,
  )}`;
}

export function ApiKeyTable({ apiKeys, scopes, onRevoke }: Props) {
  const isMobile = useBreakpointValue({ base: true, md: false });

  if (isMobile) {
    return (
      <Stack spacing={3} px={4} pb={4}>
        {apiKeys.map((apiKey) => (
          <Box
            key={apiKey.id}
            p={4}
            borderWidth="1px"
            borderRadius="12px"
            bg="white"
            _dark={{ bg: 'gray.800' }}
          >
            <Flex justify="space-between" align="flex-start" mb={2}>
              <StatusPill apiKey={apiKey} />
              {!apiKey.revokedAt && (
                <Menu>
                  <MenuButton
                    as={IconButton}
                    icon={<MdMoreVert />}
                    variant="ghost"
                    aria-label={copy.actionsMenuLabel}
                    size="sm"
                  />
                  <MenuList>
                    <MenuItem
                      color="red.600"
                      onClick={() => onRevoke(apiKey)}
                    >
                      {copy.revokeAction}
                    </MenuItem>
                  </MenuList>
                </Menu>
              )}
            </Flex>
            <Text fontWeight={600} fontSize="15px" mb={0.5}>
              {apiKey.name}
            </Text>
            <Text
              fontFamily="'JetBrains Mono', monospace"
              fontSize="12px"
              color="gray.500"
              mb={2}
            >
              {mask(apiKey)}
            </Text>
            <Box mb={2}>
              <ScopeChipList scopes={apiKey.scopes} descriptions={scopes} />
            </Box>
            <Flex justify="space-between" fontSize="12px" color="gray.600">
              <Badge
                variant="outline"
                colorScheme={
                  apiKey.environment === ApiKeyEnvironment.LIVE
                    ? 'brand'
                    : 'gray'
                }
              >
                {apiKey.environment === ApiKeyEnvironment.LIVE
                  ? copy.environment.live
                  : copy.environment.test}
              </Badge>
              <Text>{usedLabel(apiKey)}</Text>
            </Flex>
          </Box>
        ))}
      </Stack>
    );
  }

  return (
    <Box overflowX="auto" px={4} pb={4}>
      <Table size="md">
        <Thead>
          <Tr>
            <Th>{copy.columns.status}</Th>
            <Th>{copy.columns.name}</Th>
            <Th>{copy.columns.environment}</Th>
            <Th>{copy.columns.used}</Th>
            <Th>{copy.columns.createdBy}</Th>
            <Th w="48px">{copy.columns.actions}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {apiKeys.map((apiKey) => (
            <Tr
              key={apiKey.id}
              opacity={apiKey.revokedAt ? 0.55 : 1}
              _hover={{ bg: 'blackAlpha.50' }}
            >
              <Td>
                <StatusPill apiKey={apiKey} />
              </Td>
              <Td>
                <Stack spacing={1}>
                  <Tooltip label={mask(apiKey)} placement="top" hasArrow>
                    <Text fontWeight={600}>{apiKey.name}</Text>
                  </Tooltip>
                  <ScopeChipList
                    scopes={apiKey.scopes}
                    descriptions={scopes}
                  />
                </Stack>
              </Td>
              <Td>
                <Tag
                  size="sm"
                  variant={
                    apiKey.environment === ApiKeyEnvironment.LIVE
                      ? 'solid'
                      : 'outline'
                  }
                  colorScheme={
                    apiKey.environment === ApiKeyEnvironment.LIVE
                      ? 'brand'
                      : 'gray'
                  }
                >
                  {apiKey.environment === ApiKeyEnvironment.LIVE
                    ? copy.environment.live
                    : copy.environment.test}
                </Tag>
              </Td>
              <Td>
                <Text
                  fontSize="13px"
                  color={
                    apiKey.lastUsedAt &&
                    relativeDays(apiKey.lastUsedAt) > STALE_DAYS
                      ? 'orange.600'
                      : 'gray.700'
                  }
                >
                  {usedLabel(apiKey)}
                </Text>
              </Td>
              <Td>
                <Tooltip
                  label={apiKey.createdByEmail ?? copy.systemUser}
                  placement="top"
                  hasArrow
                >
                  <Text fontSize="13px" noOfLines={1} maxW="180px">
                    {apiKey.createdByEmail ?? copy.systemUser}
                  </Text>
                </Tooltip>
              </Td>
              <Td>
                {!apiKey.revokedAt && (
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      icon={<MdMoreVert />}
                      variant="ghost"
                      aria-label={`${copy.actionsMenuLabel} — ${apiKey.name}`}
                      size="sm"
                    />
                    <MenuList>
                      <MenuItem
                        color="red.600"
                        onClick={() => onRevoke(apiKey)}
                      >
                        {copy.revokeAction}
                      </MenuItem>
                    </MenuList>
                  </Menu>
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}
